import { useApolloClient, useSubscription } from '@apollo/client/react';
import {
  BroadcastOperation,
  type CompleteStylistFragment,
  GetCheckedInStylistsDocument,
  type GetCheckedInStylistsQuery,
  StylistUpdateDocument,
  type StylistUpdateFragment,
} from '@drycut/graphql';
import { useEffect } from 'react';

type Props = {
  callback?: (broadcast: StylistUpdateFragment) => void;
  slug: string;
};

export function useStylistsPubSub({ slug, callback }: Props) {
  const client = useApolloClient();

  const { data } = useSubscription(StylistUpdateDocument, { variables: { slug } });
  const broadcast: StylistUpdateFragment | undefined = data?.stylistUpdate;

  useEffect(() => {
    if (broadcast) {
      const previous = client.readQuery<GetCheckedInStylistsQuery>({
        query: GetCheckedInStylistsDocument,
      });

      const previousNodes = (previous?.checkedInStylists?.nodes || []) as CompleteStylistFragment[];

      switch (broadcast.operation) {
        case BroadcastOperation.Add: {
          // The service is already in the array, so we don't need to add it again.
          if (previousNodes.some((node) => node.id === broadcast.stylist.id)) {
            return;
          }

          const nodes = [...previousNodes, broadcast.stylist];

          client.writeQuery({
            query: GetCheckedInStylistsDocument,
            data: {
              ...previous,
              checkedInStylists: { ...previous?.checkedInStylists, nodes },
            },
          });
          break;
        }
        case BroadcastOperation.Remove: {
          client.cache.evict({ id: `Employee:${broadcast.stylist.id}` });
          client.cache.gc();

          break;
        }
        case BroadcastOperation.Update: {
          // For updates we don't need to do anything since Apollo will update the cache
          // automatically.
          break;
        }
      }

      // The callback is intentionally an imperative side effect.
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
      callback?.(broadcast);
    }
  }, [broadcast, client, callback]);
}
